import { useState, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BRAND_NAME } from "@/lib/brandConfig";
import {
  trackMediaKitGateShownPostHog,
  trackMediaKitPasswordAttemptPostHog,
} from "@/lib/posthogAnalytics";

const SESSION_KEY = "mediakit_auth";

function isAuthenticated(): boolean {
  return sessionStorage.getItem(SESSION_KEY) === "true";
}

interface Props {
  children: ReactNode;
}

const MediaKitPasswordGate = ({ children }: Props) => {
  const { t } = useTranslation();
  const [unlocked, setUnlocked] = useState(isAuthenticated);
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!unlocked) {
      trackMediaKitGateShownPostHog();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (unlocked) return <>{children}</>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const expected = import.meta.env.VITE_MEDIAKIT_PASSWORD;
    if (value === expected) {
      trackMediaKitPasswordAttemptPostHog(true);
      sessionStorage.setItem(SESSION_KEY, "true");
      setUnlocked(true);
    } else {
      trackMediaKitPasswordAttemptPostHog(false);
      setError(true);
      setValue("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black px-4">
      <div className="w-full max-w-sm flex flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="text-3xl tracking-widest font-light text-white uppercase select-none">
            {BRAND_NAME}
          </div>
          <p className="text-xs text-white/40 tracking-widest uppercase mt-1">
            {t("mediaKitGate.subtitle")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
          <Input
            type="password"
            placeholder={t("mediaKitGate.passwordPlaceholder")}
            value={value}
            autoComplete="current-password"
            autoFocus
            onChange={(e) => {
              setValue(e.target.value);
              setError(false);
            }}
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-white/20 text-center tracking-widest"
          />
          {error && (
            <p className="text-red-400 text-xs text-center tracking-wide">
              {t("mediaKitGate.wrongPassword")}
            </p>
          )}
          <Button
            type="submit"
            className="w-full bg-white text-black hover:bg-white/90 tracking-widest uppercase text-xs font-medium"
          >
            {t("mediaKitGate.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MediaKitPasswordGate;
